import { medicineCategory } from "../../utils/medicineCategory";
import TreatmentPresetBookmarks from "../../components/TreatmentPresetBookmarks";
import { applyPresetClinicalFields, mergePresetMedicines, type TreatmentPreset } from "../../utils/treatmentPresets";
import { followUpDateInput } from "../../utils/followUpDate";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  HeartPulse,
  Printer,
  RefreshCw,
  Save,
  Send,
  Stethoscope,
  Trash2,
} from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import ConfirmModal from "../../components/ConfirmModal";
import SearchSelect from "../../components/SearchSelect";
import { BACKEND_URL } from "../../config";
import BillingPrintDocument from "../../components/BillingPrintDocument";
import { useAuth } from "../../modules/auth/authContext";
import { formatPatientName } from "../../utils/patientName";
import { formatVisitLabel } from "../../utils/visitNumber";
import { isWeeklyFrequency, prescriptionDurationLabel, doseAmount, doseLabel, formatDoseSchedule, medicineDoseConfig } from "../../utils/medicineDose";
import { formatBp, formatHeightParts, formatPulse, formatSpo2, formatTempF, formatWeight } from "../../utils/vitalsFormat";

interface Patient {
  id: number;
  contact_id?: number;
  salutation?: string;
  name?: string;
  phone?: string;
  gender?: string;
  dob?: string;
  approx_age?: number | string | null;
  approx_age_indicator?: boolean;
  address?: string;
  patient_code?: string;
}

interface Visit {
  id: number;
  visit_number?: string;
  patient_id: number;
  status: string;
  visit_date?: string;
  createdAt?: string;
  assigned_doctor_id?: number;
  patient?: { Contact?: { salutation?: string; name?: string; phone?: string; gender?: string; dob?: string; address?: string }; patient_code?: string; approx_age?: number | string | null; approx_age_indicator?: boolean };
  vitals?: any[];
  clinical_record?: any;
  prescription?: { id: number; items?: any[] };
  service_orders?: Array<{ id: number; service_name: string; price?: number; quantity?: number }>;
  lab_orders?: Array<{ id: number; test_name: string }>;
}

interface Medicine {
  id: number;
  name: string;
  brand?: string;
  type?: string;
  default_meal_instruction?: "" | "before_meal" | "after_meal";
  mrpPrice?: number;
  batchNo?: string;
  expiryDate?: string;
  quantity?: number;
}

interface SearchOption {
  id: number;
  name: string;
  price?: number;
  mrpPrice?: number;
  service_name?: string;
  test_name?: string;
  quantity?: number;
  persisted?: boolean;
}

const statusLabel = (status?: string) => {
  if (!status) return "No Visit Created";
  return status
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const statusTone = (status?: string) => {
  if (!status) return "bg-background-light text-subtext-light";
  if (["VITALS_DONE", "WAITING_FOR_DOCTOR"].includes(status)) return "bg-primary/10 text-primary";
  if (["IN_CONSULTATION", "CONSULTATION_DONE", "LAB_REQUESTED"].includes(status)) return "bg-warning-amber/15 text-warning-amber";
  if (["BILLING_PENDING", "PAYMENT_PENDING"].includes(status)) return "bg-success-green/10 text-success-green";
  if (["PAID", "COMPLETED"].includes(status)) return "bg-success-green text-white";
  return "bg-background-light text-text-light";
};

const salutationOptions = ["Mr.", "Mrs.", "Ms.", "Miss", "Master", "Baby", "Dr."];
const emptyPatient = { salutation: "", name: "", phone: "", gender: "", dob: "", approx_age: "", address: "" };
const completedVisitStatuses = ["COMPLETED"];
const emptyVitals = {
  bp_systolic: "",
  bp_diastolic: "",
  pulse_rate: "",
  oxygen_saturation: "",
  temperature: "",
  height: "",
  weight: "",
  notes: "",
};

const frequencyOptions = [
  { label: "Once a day", code: "OD / q.d.", value: "1" },
  { label: "Twice a day (Every 12 hours)", code: "BD / BID", value: "2" },
  { label: "Three times a day (Every 8 hours)", code: "TD / TID", value: "3" },
  { label: "Four times a day (Every 6 hours)", code: "QDS / QID", value: "4" },
  { label: "Every 6 hours", code: "q.6.h.", value: "4" },
  { label: "As needed (Pro re nata)", code: "PRN", value: "0" },
  { label: "Once a week", code: "QWK", value: "0.14" },
];

const onceDailyFrequencyValue = "Once a day | OD / q.d. | 1";
const onceDailyTimingOptions = [
  { label: "Morning", code: "OD-M" },
  { label: "Afternoon", code: "OD-A" },
  { label: "Night", code: "OD-N" },
];

const onceDailyTimingCodes = onceDailyTimingOptions.map((option) => option.code);

const frequencyDoseCount = (frequency?: string) => {
  if (!frequency) return 0;
  if (isWeeklyFrequency(frequency)) return 1 / 7;
  if (onceDailyTimingCodes.includes(frequency)) return 1;
  const parts = frequency.split("|");
  const value = Number(parts[parts.length - 1]?.trim());
  return Number.isFinite(value) && value > 0 ? value : 0;
};

const medicineCountFromDays = (frequency: string, duration: string, countPerTime: string | number = 1) => {
  const dosesPerDay = frequencyDoseCount(frequency);
  const days = Number(duration);
  const doseCount = doseAmount(countPerTime);
  if (!dosesPerDay || !Number.isFinite(days) || days <= 0) return 1;
  return Math.max(1, Math.ceil(days * dosesPerDay * doseCount));
};

const medicineDaysFromCount = (frequency: string, quantity: string | number, countPerTime: string | number = 1) => {
  const dosesPerDay = frequencyDoseCount(frequency);
  const doseCount = doseAmount(countPerTime);
  const count = Number(quantity);
  if (!dosesPerDay || !Number.isFinite(count) || count <= 0) return "";
  return String(Math.max(1, Math.ceil(count / (dosesPerDay * doseCount))));
};

const formatDate = (value?: string | Date) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

const defaultConsultationService: SearchOption = {
  id: 0,
  name: "Consultation",
  service_name: "Consultation",
  price: 0,
  quantity: 1,
};

const instructionOptions = [
  { key: "instruction_before_meals", label: "Before meal" },
  { key: "instruction_after_meals", label: "After meal" },
  { key: "instruction_at_bedtime", label: "At bedtime" },
];

const emptyClinical = {
  chief_complaint: "",
  diagnosis: "",
  allergic: "",
  allergy_history: "",
  medication_history: "",
  on_examination: "",
  advice: "",
  follow_up_date: "",
};

const clinicalFieldLabels: Record<keyof typeof emptyClinical, string> = {
  chief_complaint: "Chief complaint",
  diagnosis: "Diagnosis",
  allergic: "Allergic",
  allergy_history: "Allergy history",
  medication_history: "Medication history",
  on_examination: "O/E",
  advice: "Advice",
  follow_up_date: "Follow-up Date",
};

const clinicalAssistedFields = ["chief_complaint", "allergic", "on_examination", "advice"] as const;
type ClinicalAssistedField = typeof clinicalAssistedFields[number];

const vitalFieldLabels: Record<keyof typeof emptyVitals, string> = {
  bp_systolic: "BP systolic",
  bp_diastolic: "BP diastolic",
  pulse_rate: "PR",
  oxygen_saturation: "SpO2",
  temperature: "Temp (⁰F)",
  height: "Height (cm)",
  weight: "Weight (kg)",
  notes: "Notes",
};

const calculateAge = (dob?: string) => {
  if (!dob) return "";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age--;
  return age >= 0 ? String(age) : "";
};

const formatAge = (patient?: Pick<Patient, "dob" | "approx_age" | "approx_age_indicator"> | null) => {
  const ageFromDob = calculateAge(patient?.dob);
  if (ageFromDob) return ageFromDob;
  if (patient?.approx_age !== undefined && patient?.approx_age !== null && String(patient.approx_age).trim()) {
    return String(patient.approx_age);
  }
  return "-";
};

const uniqueTextValues = (values: Array<unknown>) => {
  const seen = new Set<string>();
  return values.reduce<string[]>((items, value) => {
    const text = String(value || "").trim();
    if (!text || text === "-") return items;
    const key = text.toLowerCase();
    if (seen.has(key)) return items;
    seen.add(key);
    return [...items, text];
  }, []);
};

const heightCmFromVital = (vital?: any) => {
  if (!vital) return "";
  if (vital.height !== undefined && vital.height !== null && String(vital.height).trim()) return String(vital.height);
  const feet = Number(vital.height_feet || 0);
  const inches = Number(vital.height_inches || 0);
  if (!feet && !inches) return "";
  return String(Math.round((feet * 12 + inches) * 2.54));
};

const vitalsFromVisit = (visit: Visit | null) => {
  const existing = visit?.vitals?.[0];
  if (!existing) return emptyVitals;

  return {
    bp_systolic: existing.bp_systolic || "",
    bp_diastolic: existing.bp_diastolic || "",
    pulse_rate: existing.pulse_rate || "",
    oxygen_saturation: existing.oxygen_saturation || "",
    temperature: existing.temperature || "",
    height: heightCmFromVital(existing),
    weight: existing.weight || "",
    notes: existing.notes || "",
  };
};

const prescriptionItemsFromVisit = (visit: Visit | null) => {
  return (visit?.prescription?.items || []).map((item: any) => ({
    id: item.id,
    medicine_id: item.medicine_id || item.medicine?.id || 0,
    medicine_name: item.medicine_name || item.medicine?.brand || item.medicine?.name || "",
    dosage: item.dosage || "",
    frequency: item.frequency || "",
    count_per_time: item.count_per_time || 1,
    duration: item.duration || "",
    instructions: item.instructions || "",
    instruction_before_meals: Boolean(item.instruction_before_meals),
    instruction_after_meals: Boolean(item.instruction_after_meals),
    instruction_at_bedtime: Boolean(item.instruction_at_bedtime),
    instruction_details: Array.isArray(item.instruction_details) ? item.instruction_details : [],
    quantity: item.quantity || 1,
    medicine: item.medicine,
    unitPrice: item.medicine?.mrpPrice || item.unitPrice || 0,
    batchNo: item.medicine?.batchNo || item.batchNo,
    expiryDate: item.medicine?.expiryDate || item.expiryDate,
  }));
};

const medicineDisplayName = (medicine: { name?: string; brand?: string }) => {
  const name = String(medicine.name || "").trim();
  const brand = String(medicine.brand || "").trim();
  if (brand && name && brand !== name) return `${brand} - ${name}`;
  return brand || name;
};

const servicesFromVisit = (visit: Visit | null): SearchOption[] => {
  return (visit?.service_orders || []).map((service) => ({
    id: service.id,
    name: service.service_name,
    service_name: service.service_name,
    price: service.price || 0,
    quantity: service.quantity || 1,
    persisted: true,
  }));
};

const ensureDefaultConsultationService = (items: SearchOption[] = [], defaultService = defaultConsultationService) => {
  const hasConsultation = items.some((item) => String(item.service_name || item.name).trim().toLowerCase() === "consultation");
  return hasConsultation ? items : [defaultService, ...items];
};

const labsFromVisit = (visit: Visit | null): SearchOption[] => {
  return (visit?.lab_orders || []).map((lab) => ({
    id: lab.id,
    name: lab.test_name,
    test_name: lab.test_name,
    persisted: true,
  }));
};

const PatientIntake: React.FC = () => {
  const { user, hasRole, can } = useAuth();
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientForm, setPatientForm] = useState(emptyPatient);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [patientVisitHistory, setPatientVisitHistory] = useState<Visit[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [doctorId, setDoctorId] = useState("");
  const [vitalsCollapsed, setVitalsCollapsed] = useState(false);
  const [consultationCollapsed, setConsultationCollapsed] = useState(false);
  const [vitals, setVitals] = useState(emptyVitals);
  const [clinical, setClinical] = useState(emptyClinical);
  const [clinicalSuggestions, setClinicalSuggestions] = useState<Record<string, string[]>>({
    chief_complaint: [],
    allergic: [],
    on_examination: [],
    advice: [],
  });
  const [clinicalSearch, setClinicalSearch] = useState<Record<ClinicalAssistedField, string>>({
    chief_complaint: "",
    allergic: "",
    on_examination: "",
    advice: "",
  });
  const emptyMedicineDraft = {
    medicine_id: 0,
    medicine_name: "",
    frequency: "",
    once_daily_timing: "",
    count_per_time: "1",
    dosage: "",
    duration: "",
    instructions: "",
    instruction_before_meals: false,
    instruction_after_meals: false,
    instruction_at_bedtime: false,
    instruction_details: [] as typeof instructionOptions,
    quantity: 1,
    medicine: null as Medicine | null,
    unitPrice: 0,
    batchNo: "",
    expiryDate: "",
  };
  const [medicineDraft, setMedicineDraft] = useState(emptyMedicineDraft);
  const [prescriptionItems, setPrescriptionItems] = useState<any[]>([]);
  const [editingMedicineIndex, setEditingMedicineIndex] = useState<number | null>(null);
  const [serviceDraft, setServiceDraft] = useState<SearchOption | null>(null);
  const [labDraft, setLabDraft] = useState<SearchOption | null>(null);
  const [labTests, setLabTests] = useState<SearchOption[]>([]);
  const [services, setServices] = useState<SearchOption[]>([]);
  const [defaultService, setDefaultService] = useState<SearchOption>(defaultConsultationService);
  const [medicineSearchClear, setMedicineSearchClear] = useState(0);
  const [serviceSearchClear, setServiceSearchClear] = useState(0);
  const [labSearchClear, setLabSearchClear] = useState(0);
  const [patientSearchClear, setPatientSearchClear] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletePatientTarget, setDeletePatientTarget] = useState<Patient | null>(null);

  const canAdmin = hasRole("admin");
  const canFrontDesk = can("patients.vitals.edit");
  const canDoctor = can("patients.consultation.edit");
  const shouldLimitToAssignedDoctor = hasRole("doctor") && !canAdmin;
  const canViewConsultation = canFrontDesk || canDoctor;
  const pageTitle = hasRole("doctor") ? "Consultation" : "Patient Intake";

  const activeVitals = useMemo(() => selectedVisit?.vitals?.[0] || null, [selectedVisit]);
  const activeVisits = useMemo(
    () => visits.filter((visit) => !completedVisitStatuses.includes(visit.status)),
    [visits]
  );
  const savedServices = useMemo(() => servicesFromVisit(selectedVisit), [selectedVisit]);
  const savedLabTests = useMemo(() => labsFromVisit(selectedVisit), [selectedVisit]);
  const visiblePrescriptionItems = prescriptionItems;
  const visibleServices = ensureDefaultConsultationService(services.length ? services : savedServices, defaultService);
  const visibleLabTests = labTests.length ? labTests : savedLabTests;

  const api = async (path: string, options: RequestInit = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
      ...options,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const appendClinicalSuggestion = (field: keyof typeof emptyClinical, suggestion: string) => {
    setClinical((prev) => {
      const current = String(prev[field] || "");
      const parts = current.split(/([,\n])/);
      let lastTextIndex = -1;

      for (let index = parts.length - 1; index >= 0; index -= 1) {
        if (parts[index] !== "," && parts[index] !== "\n") {
          lastTextIndex = index;
          break;
        }
      }

      if (lastTextIndex === -1) return { ...prev, [field]: suggestion };

      const completedItems = parts
        .slice(0, lastTextIndex)
        .join("")
        .split(/\n|,/)
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);

      if (completedItems.includes(suggestion.trim().toLowerCase())) {
        parts[lastTextIndex] = "";
      } else {
        const leadingSpace = parts[lastTextIndex].match(/^\s*/)?.[0] || "";
        parts[lastTextIndex] = `${leadingSpace}${suggestion}`;
      }

      return { ...prev, [field]: parts.join("").replace(/[,\n]\s*$/, "") };
    });
  };

  const clinicalItems = (field: keyof typeof emptyClinical) =>
    String(clinical[field] || "")
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);

  const persistClinicalSuggestion = async (field: ClinicalAssistedField, value: string) => {
    const text = value.trim();
    if (!text) return;
    try {
      await api("/api/clinical-text-suggestions", {
        method: "POST",
        body: JSON.stringify({ category: field, text }),
      });
    } catch {
      // Suggestion persistence should not block the consultation flow.
    }
  };

  const addClinicalItem = async (field: ClinicalAssistedField, value: string) => {
    const text = value.trim();
    if (!text) return;
    const items = clinicalItems(field);
    const exists = items.some((item) => item.toLowerCase() === text.toLowerCase());
    const nextItems = exists ? items : [...items, text];
    setClinical((prev) => ({ ...prev, [field]: nextItems.join(", ") }));
    setClinicalSearch((prev) => ({ ...prev, [field]: "" }));
    if (!exists) await persistClinicalSuggestion(field, text);
  };

  const removeClinicalItem = (field: ClinicalAssistedField, value: string) => {
    const nextItems = clinicalItems(field).filter((item) => item.toLowerCase() !== value.toLowerCase());
    setClinical((prev) => ({ ...prev, [field]: nextItems.join(", ") }));
  };

  const loadVisits = async (options: { preserveSelected?: boolean } = {}) => {
    const preserveSelected = options.preserveSelected ?? true;
    setLoading(true);
    try {
      const path = shouldLimitToAssignedDoctor && user?.employee_id
        ? `/api/visits?doctor_id=${user.employee_id}&active=true&summary=true`
        : "/api/visits?active=true&summary=true";
      const data = await api(path);
      setVisits(data);
      if (preserveSelected && selectedVisit) {
        const activeVisit = data.find((visit: Visit) => visit.id === selectedVisit.id);
        if (activeVisit) {
          const fullVisit = await loadVisitWithConsultationItems(selectedVisit.id);
          setSelectedVisit(fullVisit);
          hydrateConsultationItems(fullVisit);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    const data = await api("/api/employees?type=DOCTOR");
    setDoctors(data);
  };

  const loadVisitWithConsultationItems = async (visitId: number): Promise<Visit> => {
    const [visit, prescriptions, serviceOrders, labOrders] = await Promise.all([
      api(`/api/visits/${visitId}`),
      api(`/api/prescriptions?visit_id=${visitId}`).catch(() => []),
      api(`/api/service-orders?visit_id=${visitId}`).catch(() => []),
      api(`/api/labs/orders?visit_id=${visitId}`).catch(() => []),
    ]);

    const prescriptionFromEndpoint = Array.isArray(prescriptions)
      ? prescriptions.find((prescription: any) => Array.isArray(prescription.items) && prescription.items.length) || prescriptions[0]
      : null;
    const prescriptionFromVisit = visit.prescription;

    return {
      ...visit,
      prescription: prescriptionFromEndpoint || prescriptionFromVisit || null,
      service_orders: Array.isArray(serviceOrders) && serviceOrders.length ? serviceOrders : visit.service_orders || [],
      lab_orders: Array.isArray(labOrders) && labOrders.length ? labOrders : visit.lab_orders || [],
    };
  };

  const visitSortTime = (visit: Visit) => {
    const time = new Date(visit.visit_date || visit.createdAt || "").getTime();
    return Number.isNaN(time) ? visit.id : time;
  };

  const hydrateConsultationItems = (visit: Visit) => {
    const nextPrescriptionItems = prescriptionItemsFromVisit(visit);
    setPrescriptionItems(nextPrescriptionItems);
    setServices(ensureDefaultConsultationService(servicesFromVisit(visit), defaultService));
    setLabTests(labsFromVisit(visit));
  };

  useEffect(() => {
    loadVisits();
    loadDoctors();
  }, [user?.employee_id]);

  useEffect(() => {
    api("/api/medicines/search?category=SERVICE&q=Consultation")
      .then((items) => {
        const consultation = Array.isArray(items)
          ? items.find((item: SearchOption) => String(item.name || item.service_name).trim().toLowerCase() === "consultation")
          : null;
        if (consultation) {
          setDefaultService({
            ...consultation,
            service_name: consultation.service_name || consultation.name,
            price: consultation.price || consultation.mrpPrice || 0,
          });
        }
      })
      .catch(() => setDefaultService(defaultConsultationService));
  }, []);

  useEffect(() => {
    setServices((prev) =>
      prev.map((service) =>
        service.id === 0 && String(service.service_name || service.name).trim().toLowerCase() === "consultation"
          ? defaultService
          : service
      )
    );
  }, [defaultService.id, defaultService.price, defaultService.mrpPrice]);

  const historyPatientId = selectedVisit?.patient_id || selectedPatient?.id;

  useEffect(() => {
    if (!historyPatientId) {
      setPatientVisitHistory([]);
      return;
    }

    api(`/api/visits?patient_id=${historyPatientId}`)
      .then((data) => setPatientVisitHistory(data))
      .catch(() => setPatientVisitHistory([]));
  }, [historyPatientId]);

  useEffect(() => {
    setVitals(vitalsFromVisit(selectedVisit));
    setDoctorId(selectedVisit?.assigned_doctor_id ? String(selectedVisit.assigned_doctor_id) : "");
    setVitalsCollapsed(Boolean(selectedVisit?.assigned_doctor_id));
    setConsultationCollapsed(Boolean(selectedVisit?.status && ["CONSULTATION_DONE", "BILLING_PENDING", "PAYMENT_PENDING", "PAID", "COMPLETED"].includes(selectedVisit.status)));
    if (selectedVisit?.clinical_record) {
      setClinical({
        chief_complaint: selectedVisit.clinical_record.chief_complaint || "",
        diagnosis: selectedVisit.clinical_record.diagnosis || "",
        allergic: selectedVisit.clinical_record.allergic || "",
        allergy_history: selectedVisit.clinical_record.allergy_history || "",
        medication_history: selectedVisit.clinical_record.medication_history || "",
        on_examination: selectedVisit.clinical_record.notes || "",
        advice: selectedVisit.clinical_record.follow_up_notes || "",
        follow_up_date: followUpDateInput(selectedVisit.clinical_record.follow_up_date),
      });
    } else {
      setClinical(emptyClinical);
    }
    const nextPrescriptionItems = prescriptionItemsFromVisit(selectedVisit);
    const nextServices = servicesFromVisit(selectedVisit);
    const nextLabs = labsFromVisit(selectedVisit);

    setPrescriptionItems(nextPrescriptionItems);
    setEditingMedicineIndex(null);
    setMedicineDraft(emptyMedicineDraft);
    setServices((prev) => selectedVisit ? ensureDefaultConsultationService(nextServices.length ? nextServices : prev, defaultService) : []);
    setLabTests((prev) => nextLabs.length || !selectedVisit ? nextLabs : prev);
  }, [
    selectedVisit?.id,
    selectedVisit?.status,
    selectedVisit?.clinical_record?.id,
    selectedVisit?.prescription?.id,
    selectedVisit?.prescription?.items?.length,
    selectedVisit?.service_orders?.length,
    selectedVisit?.lab_orders?.length,
  ]);

  useEffect(() => {
    const categories = clinicalAssistedFields;
    const timer = window.setTimeout(async () => {
      await Promise.all(categories.map(async (category) => {
        const selectedItems = clinicalItems(category).map((item) => item.toLowerCase());
        const query = clinicalSearch[category].trim();
        try {
          const params = new URLSearchParams({ category, q: query });
          const suggestions = await api(`/api/clinical-text-suggestions?${params.toString()}`);
          setClinicalSuggestions((prev) => ({
            ...prev,
            [category]: (suggestions || [])
              .map((item: any) => item.text)
              .filter((item: string) => item && !selectedItems.includes(item.trim().toLowerCase())),
          }));
        } catch {
          setClinicalSuggestions((prev) => ({ ...prev, [category]: [] }));
        }
      }));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    clinicalSearch.chief_complaint,
    clinicalSearch.allergic,
    clinicalSearch.on_examination,
    clinicalSearch.advice,
    clinical.chief_complaint,
    clinical.allergic,
    clinical.on_examination,
    clinical.advice,
  ]);

  const createPatient = async () => {
    if (!patientForm.name || !patientForm.phone) {
      setMessage("Name and phone are required");
      return;
    }
    if (!patientForm.dob && !String(patientForm.approx_age || "").trim()) {
      setMessage("Date of birth or approximate age is required");
      return;
    }

    const result = await api("/api/patients", {
      method: "POST",
      body: JSON.stringify({
        ...patientForm,
        approx_age: patientForm.approx_age ? Number(patientForm.approx_age) : null,
        approx_age_indicator: !patientForm.dob && Boolean(patientForm.approx_age),
      }),
    });
    const patient = {
      id: result.patient.id,
      contact_id: result.contact.id,
      salutation: result.contact.salutation,
      name: result.contact.name,
      phone: result.contact.phone,
      gender: result.contact.gender,
      dob: result.contact.dob,
      approx_age: result.patient.approx_age,
      approx_age_indicator: result.patient.approx_age_indicator,
      address: result.contact.address,
    };
    setSelectedPatient(patient);
    setSelectedVisit(null);
    setShowAddPatientModal(false);
    setPatientForm(emptyPatient);
    setMessage("Patient created");
  };

  const createVisit = async () => {
    if (!selectedPatient) return;
    const visit = await api("/api/visits", {
      method: "POST",
      body: JSON.stringify({
        patient_id: selectedPatient.id,
        visit_type: "general",
        checked_in_by_id: user?.employee_id,
        status: "VITALS_PENDING",
      }),
    });
    setSelectedVisit(visit);
    await loadVisits({ preserveSelected: false });
    setMessage("Visit created");
  };

  const patientFromVisit = (visit: Visit): Patient => ({
    id: visit.patient_id,
    salutation: visit.patient?.Contact?.salutation,
    name: visit.patient?.Contact?.name,
    phone: visit.patient?.Contact?.phone,
    gender: visit.patient?.Contact?.gender,
    dob: visit.patient?.Contact?.dob,
    approx_age: visit.patient?.approx_age,
    approx_age_indicator: visit.patient?.approx_age_indicator,
    address: visit.patient?.Contact?.address,
    patient_code: visit.patient?.patient_code,
  });

  const chooseVisit = async (visit: Visit) => {
    const summaryPatient = patientFromVisit(visit);
    setSelectedPatient(summaryPatient);
    setPatientForm({
      salutation: summaryPatient.salutation || "",
      name: summaryPatient.name || "",
      phone: summaryPatient.phone || "",
      gender: summaryPatient.gender || "",
      dob: summaryPatient.dob || "",
      approx_age: summaryPatient.approx_age ? String(summaryPatient.approx_age) : "",
      address: summaryPatient.address || "",
    });

    try {
      const fullVisit = await loadVisitWithConsultationItems(visit.id);
      const patient = patientFromVisit(fullVisit);
      setSelectedPatient(patient);
      setSelectedVisit(fullVisit);
      hydrateConsultationItems(fullVisit);
      setPatientForm({
        salutation: patient.salutation || "",
        name: patient.name || "",
        phone: patient.phone || "",
        gender: patient.gender || "",
        dob: patient.dob || "",
        approx_age: patient.approx_age ? String(patient.approx_age) : "",
        address: patient.address || "",
      });
    } catch (err: any) {
      setMessage(`Failed to load visit details: ${err.message}`);
    }
  };

  const returnToActivePatients = async () => {
    setSelectedPatient(null);
    setSelectedVisit(null);
    setPatientVisitHistory([]);
    setPatientForm(emptyPatient);
    setPatientSearchClear((value) => value + 1);
    setMessage("");
    await loadVisits();
  };

  const resetPatientSelection = () => {
    setSelectedPatient(null);
    setSelectedVisit(null);
    setPatientVisitHistory([]);
    setPatientForm(emptyPatient);
    setPatientSearchClear((value) => value + 1);
    setVitals(emptyVitals);
    setClinical(emptyClinical);
    setPrescriptionItems([]);
    setServices([]);
    setLabTests([]);
    setDoctorId("");
    setVitalsCollapsed(false);
    setConsultationCollapsed(false);
  };

  const refreshPatientIntake = async () => {
    resetPatientSelection();
    setMessage("");
    await loadVisits({ preserveSelected: false });
  };

  const deletePatient = async () => {
    if (!canAdmin || !deletePatientTarget) return;
    const patient = deletePatientTarget;
    setDeletePatientTarget(null);
    await api(`/api/patients/${patient.id}`, { method: "DELETE" });
    if (selectedPatient?.id === patient.id || selectedVisit?.patient_id === patient.id) {
      resetPatientSelection();
    }
    setVisits((prev) => prev.filter((visit) => visit.patient_id !== patient.id));
    setMessage(`${formatPatientName(patient, patient.name || "Patient")} deleted`);
    await loadVisits({ preserveSelected: false });
  };

  const openAddPatientModal = () => {
    setPatientForm(emptyPatient);
    setShowAddPatientModal(true);
  };

  const reloadPatientIntake = () => {
    window.location.href = "/patients/intake";
  };

  const saveVitals = async () => {
    if (!selectedVisit) return;

    await api("/api/vitals", {
      method: "POST",
      body: JSON.stringify(vitalsPayload(selectedVisit.id)),
    });
    await loadVisits();
    setMessage("Vitals saved");
  };

  const vitalsPayload = (visitId: number) => {
    return {
      bp_systolic: vitals.bp_systolic || null,
      bp_diastolic: vitals.bp_diastolic || null,
      pulse_rate: vitals.pulse_rate || null,
      oxygen_saturation: vitals.oxygen_saturation || null,
      temperature: vitals.temperature || null,
      height: vitals.height || null,
      weight: vitals.weight || null,
      notes: vitals.notes || null,
      visit_id: visitId,
      recorded_by_id: user?.employee_id,
    };
  };

  const assignDoctor = async () => {
    if (!selectedVisit || !doctorId) return;
    await api("/api/vitals", {
      method: "POST",
      body: JSON.stringify(vitalsPayload(selectedVisit.id)),
    });
    const updatedVisit = await api(`/api/visits/${selectedVisit.id}/assign-doctor`, {
      method: "POST",
      body: JSON.stringify({ doctor_id: Number(doctorId), assigned_by_id: user?.employee_id }),
    });
    setSelectedVisit(updatedVisit);
    setVitalsCollapsed(true);
    await loadVisits();
    setMessage("Doctor assigned");
  };

  const persistConsultationData = async (completeVisit: boolean) => {
    if (!selectedVisit) return null;
    if (!canDoctor) {
      setMessage("Only doctors can update consultation details");
      return null;
    }
    try {
      const consultationDoctorId = user?.employee_id || selectedVisit.assigned_doctor_id || Number(doctorId) || null;
      if (!consultationDoctorId) {
        setMessage("Assign a doctor before saving consultation");
        return null;
      }

      const clinicalPayload = {
        chief_complaint: clinical.chief_complaint,
        diagnosis: clinical.diagnosis,
        allergic: clinical.allergic,
        allergy_history: clinical.allergy_history,
        medication_history: clinical.medication_history,
        notes: clinical.on_examination,
        follow_up_notes: clinical.advice,
        follow_up_date: clinical.follow_up_date || null,
        visit_id: selectedVisit.id,
        doctor_id: consultationDoctorId,
      };

      await Promise.all(
        clinicalAssistedFields.flatMap((field) =>
          clinicalItems(field).map((item) => persistClinicalSuggestion(field, item))
        )
      );

      if (selectedVisit.clinical_record?.id) {
        await api(`/api/clinical-records/${selectedVisit.clinical_record.id}`, {
          method: "PUT",
          body: JSON.stringify(clinicalPayload),
        });
      } else {
        await api("/api/clinical-records", {
          method: "POST",
          body: JSON.stringify(clinicalPayload),
        });
      }

      const consultationPrescriptionItems = prescriptionItems;
      const consultationServices = ensureDefaultConsultationService(services.length ? services : savedServices, defaultService);
      const consultationLabTests = labTests.length ? labTests : savedLabTests;

      if (consultationPrescriptionItems.length || selectedVisit.prescription?.id) {
        const prescriptionPayload = {
          visit_id: selectedVisit.id,
          doctor_id: consultationDoctorId,
          status: "draft",
          items: consultationPrescriptionItems,
        };

        if (selectedVisit.prescription?.id) {
          await api(`/api/prescriptions/${selectedVisit.prescription.id}`, {
            method: "PUT",
            body: JSON.stringify(prescriptionPayload),
          });
        } else {
          await api("/api/prescriptions", {
            method: "POST",
            body: JSON.stringify(prescriptionPayload),
          });
        }
      }

      for (const labTest of consultationLabTests.filter((item) => !item.persisted)) {
        await api("/api/labs/orders", {
          method: "POST",
          body: JSON.stringify({
            visit_id: selectedVisit.id,
            patient_id: selectedVisit.patient_id,
            doctor_id: consultationDoctorId,
            test_name: labTest.test_name || labTest.name,
          }),
        });
      }

      const existingServiceNames = new Set(
        [...savedServices, ...servicesFromVisit(selectedVisit)]
          .map((item) => String(item.service_name || item.name || "").trim().toLowerCase())
          .filter(Boolean)
      );
      const postedServiceNames = new Set<string>();

      for (const service of consultationServices.filter((item) => !item.persisted)) {
        const serviceName = String(service.service_name || service.name || "").trim();
        const serviceKey = serviceName.toLowerCase();
        if (!serviceName || existingServiceNames.has(serviceKey) || postedServiceNames.has(serviceKey)) continue;
        postedServiceNames.add(serviceKey);
        await api("/api/service-orders", {
          method: "POST",
          body: JSON.stringify({
            visit_id: selectedVisit.id,
            doctor_id: consultationDoctorId,
            service_name: serviceName,
            price: service.price || service.mrpPrice || 0,
            quantity: service.quantity || 1,
          }),
        });
      }

      if (completeVisit) {
        return api(`/api/visits/${selectedVisit.id}/complete-consultation`, { method: "POST" });
      }

      return loadVisitWithConsultationItems(selectedVisit.id);
    } catch (err: any) {
      setMessage(`Failed to save consultation: ${err.message}`);
      return null;
    }
  };

  const saveConsultation = async () => {
    const updatedVisit = await persistConsultationData(true);
    if (updatedVisit) {
      const fullVisit = await loadVisitWithConsultationItems(updatedVisit.id);
      setSelectedVisit(fullVisit);
      hydrateConsultationItems(fullVisit);
      setConsultationCollapsed(true);
      await loadVisits();
      setMessage("Consultation saved");
    }
  };

  const addMedicineDraft = () => {
    if (!canDoctor) return;
    if (!medicineDraft.medicine_name.trim()) return;
    const doseConfig = medicineDoseConfig(medicineDraft.medicine);
    if (doseConfig.custom && !medicineDraft.dosage.trim()) {
      setMessage("Enter the prescribed dose including its unit");
      return;
    }
    const amount = Number(medicineDraft.count_per_time);
    if (!Number.isFinite(amount) || amount <= 0 || (!doseConfig.decimal && !Number.isInteger(amount))) {
      setMessage(`Enter a positive ${doseConfig.decimal ? "amount" : "whole number"} in ${doseConfig.plural} per time`);
      return;
    }
    if (medicineDraft.frequency === onceDailyFrequencyValue && !medicineDraft.once_daily_timing) {
      setMessage("Select morning, afternoon, or night for once daily medicine");
      return;
    }
    const frequency = medicineDraft.frequency === onceDailyFrequencyValue && medicineDraft.once_daily_timing
      ? medicineDraft.once_daily_timing
      : medicineDraft.frequency;
    const { once_daily_timing: _onceDailyTiming, ...medicineItem } = {
      ...medicineDraft,
      frequency,
      count_per_time: amount,
    };
    setPrescriptionItems((prev) => editingMedicineIndex === null ? [...prev, medicineItem] : prev.map((item, index) => index === editingMedicineIndex ? medicineItem : item));
    setEditingMedicineIndex(null);
    setMedicineDraft(emptyMedicineDraft);
    setMedicineSearchClear((value) => value + 1);
  };

  const updateMedicineFrequency = (frequency: string) => {
    setMedicineDraft((prev) => {
      const displayedDuration = prev.duration ? Number(prev.duration) / (isWeeklyFrequency(prev.frequency) ? 7 : 1) : 0;
      const duration = displayedDuration ? String(displayedDuration * (isWeeklyFrequency(frequency) ? 7 : 1)) : "";
      return {
        ...prev,
        frequency,
        duration,
        once_daily_timing: frequency === onceDailyFrequencyValue ? prev.once_daily_timing : "",
        quantity: medicineDoseConfig(prev.medicine).autoQuantity && frequencyDoseCount(frequency) && duration ? medicineCountFromDays(frequency, duration, prev.count_per_time) : prev.quantity,
      };
    });
  };

  const loadTreatmentPreset = (complaint: string, preset: TreatmentPreset) => {
    if (!canDoctor || !selectedVisit) return;
    setClinical((prev) => applyPresetClinicalFields(prev, complaint, preset));
    setPrescriptionItems((prev) => mergePresetMedicines(prev, preset));
  };

  const editPrescriptionMedicine = (index: number) => {
    const item = prescriptionItems[index];
    const timed = onceDailyTimingCodes.includes(item.frequency);
    setMedicineDraft({ ...emptyMedicineDraft, ...item, count_per_time: String(item.count_per_time || 1),
      dosage: item.dosage || "", frequency: timed ? onceDailyFrequencyValue : item.frequency,
      once_daily_timing: timed ? item.frequency : "" });
    setEditingMedicineIndex(index);
    document.getElementById("consultation-medicine-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateMedicineDuration = (duration: string) => {
    setMedicineDraft((prev) => ({
      ...prev,
      duration,
      quantity: medicineDoseConfig(prev.medicine).autoQuantity && frequencyDoseCount(prev.frequency) && duration ? medicineCountFromDays(prev.frequency, duration, prev.count_per_time) : prev.quantity,
    }));
  };

  const updateMedicineCountPerTime = (countPerTime: string) => {
    const numericCount = Number(countPerTime);
    setMedicineDraft((prev) => ({
      ...prev,
      count_per_time: countPerTime,
      quantity: medicineDoseConfig(prev.medicine).autoQuantity && Number.isFinite(numericCount) && numericCount > 0 && frequencyDoseCount(prev.frequency) && prev.duration ? medicineCountFromDays(prev.frequency, prev.duration, numericCount) : prev.quantity,
    }));
  };

  const updateMedicineQuantity = (quantity: string) => {
    const numericQuantity = Number(quantity);
    const nextQuantity = Number.isFinite(numericQuantity) && numericQuantity > 0 ? Math.ceil(numericQuantity) : 1;
    setMedicineDraft((prev) => ({
      ...prev,
      quantity: nextQuantity,
      duration: medicineDoseConfig(prev.medicine).autoQuantity && frequencyDoseCount(prev.frequency) ? medicineDaysFromCount(prev.frequency, nextQuantity, prev.count_per_time) : prev.duration,
    }));
  };

  const updateServiceQuantity = (quantity: string) => {
    const numericQuantity = Number(quantity);
    const nextQuantity = Number.isFinite(numericQuantity) && numericQuantity > 0 ? Math.ceil(numericQuantity) : 1;
    setServiceDraft((prev) => (prev ? { ...prev, quantity: nextQuantity } : prev));
  };

  const addServiceDraft = () => {
    if (!canDoctor) return;
    if (!serviceDraft) return;
    const serviceQuantity = Math.max(1, Math.ceil(Number(serviceDraft.quantity || 1)));
    const nextService = { ...serviceDraft, quantity: serviceQuantity };
    setServices((prev) => {
      const base = ensureDefaultConsultationService(prev.length ? prev : savedServices, defaultService);
      return base.some((item) => item.name === serviceDraft.name) ? base : [...base, nextService];
    });
    setServiceDraft(null);
    setServiceSearchClear((value) => value + 1);
  };

  const addLabDraft = () => {
    if (!canDoctor) return;
    if (!labDraft) return;
    setLabTests((prev) => {
      const base = prev.length ? prev : savedLabTests;
      return base.some((item) => item.name === labDraft.name) ? base : [...base, labDraft];
    });
    setLabDraft(null);
    setLabSearchClear((value) => value + 1);
  };

  const sendToBilling = async () => {
    if (!selectedVisit) return;
    try {
      const savedVisit = await persistConsultationData(false);
      if (!savedVisit) return;
      const updatedVisit = await api(`/api/visits/${selectedVisit.id}/send-to-billing`, { method: "POST" });
      const fullVisit = await loadVisitWithConsultationItems(updatedVisit.id);
      setSelectedVisit(fullVisit);
      hydrateConsultationItems(fullVisit);
      setConsultationCollapsed(true);
      await loadVisits();
      setMessage("Sent to billing");
    } catch (err: any) {
      setMessage(`Failed to send to billing: ${err.message}`);
    }
  };

  const editConsultation = async () => {
    if (!canDoctor) return;
    if (!selectedVisit) return;
    try {
      const fullVisit = await loadVisitWithConsultationItems(selectedVisit.id);
      hydrateConsultationItems(fullVisit);
      setSelectedVisit(fullVisit);
      setConsultationCollapsed(false);
    } catch (err: any) {
      setMessage(`Failed to load consultation details: ${err.message}`);
      setConsultationCollapsed(false);
    }
  };

  const loadLatestVisitForPatient = async (patientId: number) => {
    const patientVisits = await api(`/api/visits?patient_id=${patientId}&active=true`);
    setVisits(patientVisits);
    setSelectedVisit(patientVisits[0] || null);
  };

  const displayPatient = selectedPatient || (selectedVisit?.patient
    ? {
        id: selectedVisit.patient_id,
        salutation: selectedVisit.patient.Contact?.salutation,
        name: selectedVisit.patient.Contact?.name,
        phone: selectedVisit.patient.Contact?.phone,
        gender: selectedVisit.patient.Contact?.gender,
        dob: selectedVisit.patient.Contact?.dob,
        approx_age: selectedVisit.patient.approx_age,
        approx_age_indicator: selectedVisit.patient.approx_age_indicator,
        address: selectedVisit.patient.Contact?.address,
      }
    : null);
  const patientName = formatPatientName(displayPatient, "No patient selected");
  const assignedDoctor = doctors.find((doctor: any) => String(doctor.id) === String(doctorId || selectedVisit?.assigned_doctor_id));
  const visitHistory = (patientVisitHistory.length ? patientVisitHistory : visits)
    .filter((visit) => visit.patient_id === selectedVisit?.patient_id && visit.id !== selectedVisit?.id)
    .sort((a, b) => visitSortTime(b) - visitSortTime(a) || b.id - a.id);
  const patientClinicalRecords = useMemo(() => {
    const records = [...patientVisitHistory, ...(selectedVisit ? [selectedVisit] : [])]
      .map((visit) => visit.clinical_record)
      .filter(Boolean);
    return records;
  }, [patientVisitHistory, selectedVisit?.id, selectedVisit?.clinical_record?.id]);
  const uniqueAllergies = useMemo(
    () => uniqueTextValues(patientClinicalRecords.flatMap((record) => [record.allergy_history, record.allergic === "Yes" ? "Allergic" : ""])),
    [patientClinicalRecords]
  );
  const uniqueMedicalHistory = useMemo(
    () => uniqueTextValues(patientClinicalRecords.map((record) => record.medication_history)),
    [patientClinicalRecords]
  );
  const summaryVitals = activeVitals || {
    bp_systolic: vitals.bp_systolic,
    bp_diastolic: vitals.bp_diastolic,
    pulse_rate: vitals.pulse_rate,
    oxygen_saturation: vitals.oxygen_saturation,
    temperature: vitals.temperature,
    height: vitals.height,
    weight: vitals.weight,
  };
  const activeClinical = {
    chief_complaint: clinical.chief_complaint || selectedVisit?.clinical_record?.chief_complaint || "",
    diagnosis: clinical.diagnosis || selectedVisit?.clinical_record?.diagnosis || "",
    on_examination: clinical.on_examination || selectedVisit?.clinical_record?.notes || "",
    advice: clinical.advice || selectedVisit?.clinical_record?.follow_up_notes || "",
    follow_up_date: clinical.follow_up_date,
  };
  const printConsultationDocuments = async () => {
    if (!selectedVisit) return;
    await document.fonts.ready;
    await Promise.all(Array.from(document.images).map((image) => image.decode().catch(() => undefined)));
    window.print();
  };

  return (
    <SidebarLayout title={pageTitle} subtitle="Check in patients, capture vitals, and complete consultation workflow.">
      <div className="flex w-full flex-col gap-6 print:hidden">
        <Modal open={showAddPatientModal} title="Add New Patient" message="Please add patient details" onClose={() => setShowAddPatientModal(false)}>
          <div className="grid w-[min(90vw,760px)] grid-cols-1 gap-3 sm:grid-cols-2">
            <select value={patientForm.salutation} onChange={(e) => setPatientForm((p) => ({ ...p, salutation: e.target.value }))} className="rounded border p-2">
              <option value="">Select Salutation</option>
              {salutationOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <input placeholder="Name" value={patientForm.name} onChange={(e) => setPatientForm((p) => ({ ...p, name: e.target.value }))} className="rounded border p-2" />
            <input placeholder="Phone" value={patientForm.phone} onChange={(e) => setPatientForm((p) => ({ ...p, phone: e.target.value }))} className="rounded border p-2" />
            <select value={patientForm.gender} onChange={(e) => setPatientForm((p) => ({ ...p, gender: e.target.value }))} className="rounded border p-2">
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <label>
              <div className="mb-1 text-sm font-semibold">Date of Birth</div>
              <input
                type="date"
                value={patientForm.dob}
                onChange={(e) => setPatientForm((p) => ({ ...p, dob: e.target.value, approx_age: e.target.value ? "" : p.approx_age }))}
                className="w-full rounded border p-2"
              />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Age</div>
              <input
                type="number"
                min="0"
                max="130"
                value={patientForm.approx_age}
                onChange={(e) => setPatientForm((p) => ({ ...p, approx_age: e.target.value, dob: e.target.value ? "" : p.dob }))}
                className="w-full rounded border p-2"
                placeholder="Enter if DOB unknown"
              />
            </label>
            <textarea placeholder="Address" value={patientForm.address} onChange={(e) => setPatientForm((p) => ({ ...p, address: e.target.value }))} className="rounded border p-2 sm:col-span-2" />
            <button className="rounded bg-primary px-4 py-2 text-white sm:col-span-2" onClick={createPatient}>Add Patient</button>
          </div>
        </Modal>
        <ConfirmModal
          open={Boolean(deletePatientTarget)}
          message={`Delete ${formatPatientName(deletePatientTarget, deletePatientTarget?.name || "this patient")}?`}
          onCancel={() => setDeletePatientTarget(null)}
          onConfirm={deletePatient}
        />

        <section className="rounded border border-border-light bg-surface-light p-5">
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(420px,560px)] xl:items-end">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <div className="text-sm font-bold uppercase tracking-wide text-subtext-light">Patient</div>
                {selectedVisit && (
                  <>
                    <button
                      type="button"
                      onClick={reloadPatientIntake}
                      className="inline-flex items-center gap-2 rounded border border-border-light px-3 py-1.5 text-sm font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
                    >
                      <ArrowLeft size={16} />
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={returnToActivePatients}
                      className="inline-flex items-center gap-2 rounded border border-border-light px-3 py-1.5 text-sm font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
                    >
                      <Home size={16} />
                      Active Patients
                    </button>
                  </>
                )}
              </div>
              <h2 className="truncate text-2xl font-black text-text-light">{patientName}</h2>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-subtext-light">
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${statusTone(selectedVisit?.status)}`}>
                  {statusLabel(selectedVisit?.status)}
                </span>
                {selectedVisit && <span>{formatVisitLabel(selectedVisit)}</span>}
                {displayPatient?.phone && <span>Phone: {displayPatient.phone}</span>}
                {displayPatient && <span>Age: {formatAge(displayPatient)}</span>}
                {displayPatient?.address && <span className="max-w-full truncate">Address: {displayPatient.address}</span>}
              </div>
              {canAdmin && displayPatient && (
                <button
                  type="button"
                  onClick={() => setDeletePatientTarget(displayPatient)}
                  className="mt-3 inline-flex items-center gap-2 rounded border border-alert-red/30 px-3 py-2 text-sm font-semibold text-alert-red hover:bg-alert-red/10"
                >
                  <Trash2 size={16} />
                  Delete Patient
                </button>
              )}
            </div>

            {canFrontDesk && (
              <div className="min-w-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <SearchSelect<Patient>
                      label="Find Patient"
                      placeholder="Name / Phone / Address"
                      fetchUrl={`${BACKEND_URL}/api/patients/search`}
                      displayKey="name"
                      clearSignal={patientSearchClear}
                      onSelect={(patient) => {
                        setSelectedPatient(patient);
                        loadLatestVisitForPatient(patient.id);
                        setPatientForm({
                          salutation: patient.salutation || "",
                          name: patient.name || "",
                          phone: patient.phone || "",
                          gender: patient.gender || "",
                          dob: patient.dob || "",
                          approx_age: patient.approx_age ? String(patient.approx_age) : "",
                          address: patient.address || "",
                        });
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-none">
                    <button type="button" onClick={openAddPatientModal} className="rounded bg-green-500 px-4 py-3 text-sm font-semibold text-white">
                      + Add
                    </button>
                    <button onClick={refreshPatientIntake} className="inline-flex items-center justify-center gap-2 rounded border border-border-light px-4 py-3 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
                      <RefreshCw size={18} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {displayPatient && (
          <section className="rounded border border-border-light bg-surface-light p-5">
            <h3 className="mb-4 text-lg font-bold text-text-light">Allergy & Medical History</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="mb-2 text-sm font-bold uppercase tracking-wide text-subtext-light">Allergy</div>
                {uniqueAllergies.length ? (
                  <div className="flex flex-wrap gap-2">
                    {uniqueAllergies.map((item) => (
                      <span key={item} className="rounded-full bg-alert-red/10 px-3 py-1 text-sm font-semibold text-alert-red">
                        {item}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-subtext-light">-</div>
                )}
              </div>
              <div>
                <div className="mb-2 text-sm font-bold uppercase tracking-wide text-subtext-light">Medical History</div>
                {uniqueMedicalHistory.length ? (
                  <div className="flex flex-wrap gap-2">
                    {uniqueMedicalHistory.map((item) => (
                      <span key={item} className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                        {item}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-subtext-light">-</div>
                )}
              </div>
            </div>
          </section>
        )}

        {!selectedVisit && (
          <section className="rounded border border-border-light bg-surface-light p-5">
            <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <div>
                <h3 className="inline-flex rounded bg-warning-amber px-3 py-1.5 text-xl font-bold text-white shadow-sm">Active Patients</h3>
                <p className="mt-1 text-sm text-subtext-light">Choose a patient to open vitals and doctor consultation.</p>
              </div>
              {loading && <span className="text-sm font-semibold text-subtext-light">Loading...</span>}
            </div>

            {activeVisits.length ? (
              <div className="overflow-hidden rounded border border-border-light">
                <div className={`hidden gap-3 bg-background-light px-4 py-3 text-xs font-bold uppercase tracking-wide text-subtext-light md:grid ${canAdmin ? "grid-cols-[minmax(0,1.5fr)_120px_100px_160px_120px_90px]" : "grid-cols-[minmax(0,1.5fr)_120px_100px_160px_120px]"}`}>
                  <span>Patient</span>
                  <span>Phone</span>
                  <span>Age</span>
                  <span>Status</span>
                  <span>Visit</span>
                  {canAdmin && <span>Actions</span>}
                </div>
                <div className="divide-y divide-border-light">
                  {activeVisits.map((visit) => {
                    const patient = patientFromVisit(visit);
                    return (
                      <div
                        key={visit.id}
                        className={`grid w-full grid-cols-1 gap-2 px-4 py-3 text-left hover:bg-primary/5 md:items-center md:gap-3 ${canAdmin ? "md:grid-cols-[minmax(0,1.5fr)_120px_100px_160px_120px_90px]" : "md:grid-cols-[minmax(0,1.5fr)_120px_100px_160px_120px]"}`}
                      >
                        <button type="button" onClick={() => chooseVisit(visit)} className="min-w-0 text-left">
                          <span className="block truncate font-semibold text-text-light">{formatPatientName(patient, "Unnamed Patient")}</span>
                          {patient.patient_code && <span className="block text-xs text-subtext-light">{patient.patient_code}</span>}
                        </button>
                        <span className="text-sm text-subtext-light">{patient.phone || "-"}</span>
                        <span className="text-sm text-subtext-light">{formatAge(patient)}</span>
                        <span>
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${statusTone(visit.status)}`}>
                            {statusLabel(visit.status)}
                          </span>
                        </span>
                        <span className="text-sm font-semibold text-subtle-light">{formatVisitLabel(visit)}</span>
                        {canAdmin && (
                          <button
                            type="button"
                            onClick={() => setDeletePatientTarget(patient)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded border border-alert-red/30 text-alert-red hover:bg-alert-red/10"
                            title="Delete patient"
                            aria-label={`Delete ${formatPatientName(patient, "patient")}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded border border-dashed border-border-light bg-background-light p-5 text-sm text-subtext-light">
                No active patients found.
              </div>
            )}
          </section>
        )}

        {selectedVisit && vitalsCollapsed && (
          <section className="rounded border border-border-light bg-surface-light p-5">
            <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <h3 className="flex items-center gap-2 text-xl font-bold">
                <HeartPulse className="text-primary" size={22} />
                Vitals Summary
              </h3>
              {canFrontDesk && (
                <button
                  onClick={() => setVitalsCollapsed(false)}
                  className="rounded border border-border-light px-3 py-2 text-sm font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
                >
                  Edit Vitals
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 rounded bg-background-light p-3 text-sm md:grid-cols-4 xl:grid-cols-7">
              <div><span className="text-subtext-light">BP</span><div className="font-bold">{formatBp(summaryVitals.bp_systolic, summaryVitals.bp_diastolic)}</div></div>
              <div><span className="text-subtext-light">PR</span><div className="font-bold">{formatPulse(summaryVitals.pulse_rate)}</div></div>
              <div><span className="text-subtext-light">SpO2</span><div className="font-bold">{formatSpo2(summaryVitals.oxygen_saturation)}</div></div>
              <div><span className="text-subtext-light">Temp</span><div className="font-bold">{formatTempF(summaryVitals.temperature)}</div></div>
              {summaryVitals.height && <div><span className="text-subtext-light">Height</span><div className="font-bold">{formatHeightParts(undefined, undefined, summaryVitals.height)}</div></div>}
              <div><span className="text-subtext-light">Weight</span><div className="font-bold">{formatWeight(summaryVitals.weight)}</div></div>
              <div><span className="text-subtext-light">Doctor</span><div className="font-bold">{assignedDoctor?.name || selectedVisit.assigned_doctor_id || "-"}</div></div>
            </div>
          </section>
        )}

        {canFrontDesk && selectedVisit && !vitalsCollapsed && (
          <section className="rounded border border-border-light bg-surface-light p-5">
            <h3 className="mb-4 flex items-center gap-2 text-xl font-bold">
              <HeartPulse className="text-primary" size={22} />
              Vitals
            </h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-[repeat(7,96px)]">
              {Object.keys(emptyVitals).map((field) => (
                <label key={field} className={field === "notes" ? "col-span-full" : ""}>
                  <div className="mb-1 text-sm font-semibold">{vitalFieldLabels[field as keyof typeof emptyVitals]}</div>
                  {field === "notes" ? (
                    <textarea
                      value={(vitals as any)[field]}
                      onChange={(e) => setVitals((prev) => ({ ...prev, [field]: e.target.value }))}
                      className="min-h-24 w-full resize-none rounded border border-border-light bg-background-light p-3"
                    />
                  ) : (
                    <input
                      value={(vitals as any)[field]}
                      onChange={(e) => setVitals((prev) => ({ ...prev, [field]: e.target.value }))}
                      className="h-10 w-full max-w-24 rounded border border-border-light bg-background-light px-2 text-sm"
                    />
                  )}
                </label>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={saveVitals} className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 font-semibold text-white">
                <Save size={18} />
                Save Vitals
              </button>
              <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="rounded border border-border-light bg-background-light px-3 py-2">
                <option value="">Assign doctor</option>
                {doctors.map((doctor: any) => <option key={doctor.id} value={doctor.id}>{doctor.name}</option>)}
              </select>
              <button onClick={assignDoctor} className="inline-flex items-center gap-2 rounded border border-border-light px-4 py-2 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
                <Send size={18} />
                Send to Doctor
              </button>
            </div>
          </section>
        )}

        <div className="flex flex-col gap-6">
          <main className="flex flex-col gap-6">
            {canFrontDesk && selectedPatient && !selectedVisit && (
              <section className="rounded border border-dashed border-border-light bg-surface-light p-5">
                <h3 className="text-xl font-bold text-text-light">No Active Visit</h3>
                <p className="mt-1 text-sm text-subtext-light">Create a visit to start vitals and doctor assignment for this patient.</p>
                <button
                  onClick={createVisit}
                  className="mt-4 inline-flex items-center justify-center rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                >
                  Add Visit
                </button>
              </section>
            )}

            {canViewConsultation && selectedVisit && (consultationCollapsed || !canDoctor) && (
              <section className="rounded border border-border-light bg-surface-light p-5">
                <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <h3 className="flex items-center gap-2 text-xl font-bold">
                    <Stethoscope className="text-primary" size={22} />
                    Consultation Summary
                  </h3>
                  {canDoctor && (
                    <button
                      onClick={editConsultation}
                      className="rounded border border-border-light px-3 py-2 text-sm font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
                    >
                      Edit Consultation
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 rounded bg-background-light p-3 text-sm md:grid-cols-4">
                  <div>
                    <span className="text-subtext-light">Chief complaint</span>
                    <div className="font-bold">{clinical.chief_complaint || selectedVisit.clinical_record?.chief_complaint || "-"}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Diagnosis</span>
                    <div className="font-bold">{clinical.diagnosis || selectedVisit.clinical_record?.diagnosis || "-"}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Allergic</span>
                    <div className="font-bold">{clinical.allergic || selectedVisit.clinical_record?.allergic || "-"}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Allergy history</span>
                    <div className="font-bold">{clinical.allergy_history || selectedVisit.clinical_record?.allergy_history || "-"}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Medication history</span>
                    <div className="font-bold">{clinical.medication_history || selectedVisit.clinical_record?.medication_history || "-"}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Medicines</span>
                    <div className="font-bold">{visiblePrescriptionItems.length}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Services</span>
                    <div className="font-bold">{visibleServices.length}</div>
                  </div>
                  <div>
                    <span className="text-subtext-light">Labs</span>
                    <div className="font-bold">{visibleLabTests.length}</div>
                  </div>
                </div>
              </section>
            )}

            {canDoctor && selectedVisit && !consultationCollapsed && (
              <section className="rounded border border-border-light bg-surface-light p-5">
                <h3 className="mb-4 flex items-center gap-2 text-xl font-bold">
                  <Stethoscope className="text-primary" size={22} />
                  Doctor Consultation
                </h3>
                <TreatmentPresetBookmarks key={selectedVisit.id} onLoad={loadTreatmentPreset} />
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
                  <div className="grid grid-cols-1 gap-4">
                    {Object.keys(clinical).map((field) => (
                      <label key={field}>
                        <div className="mb-1 text-sm font-semibold">{clinicalFieldLabels[field as keyof typeof emptyClinical]}</div>
                        {field === "follow_up_date" ? (
                          <input
                            type="date"
                            value={clinical.follow_up_date}
                            onChange={(e) => setClinical((prev) => ({ ...prev, follow_up_date: e.target.value }))}
                            className="w-full rounded border border-border-light bg-background-light p-3"
                          />
                        ) : clinicalAssistedFields.includes(field as ClinicalAssistedField) ? (
                          <div className="rounded border border-border-light bg-background-light p-3">
                            <div className="mb-2 flex min-h-9 flex-wrap gap-2">
                              {clinicalItems(field as keyof typeof emptyClinical).map((item) => (
                                <span key={item} className="inline-flex items-center gap-2 rounded bg-primary/10 px-2 py-1 text-sm font-semibold text-primary">
                                  {item}
                                  <button type="button" onClick={() => removeClinicalItem(field as ClinicalAssistedField, item)} className="text-primary hover:text-alert-red">x</button>
                                </span>
                              ))}
                              {!clinicalItems(field as keyof typeof emptyClinical).length && <span className="text-sm text-subtext-light">No items added.</span>}
                            </div>
                            <input
                              value={clinicalSearch[field as ClinicalAssistedField]}
                              onChange={(e) => setClinicalSearch((prev) => ({ ...prev, [field]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === ",") {
                                  e.preventDefault();
                                  addClinicalItem(field as ClinicalAssistedField, clinicalSearch[field as ClinicalAssistedField]);
                                }
                              }}
                              onBlur={() => addClinicalItem(field as ClinicalAssistedField, clinicalSearch[field as ClinicalAssistedField])}
                              className="w-full rounded border border-border-light bg-white p-2"
                              placeholder={`Search or add ${clinicalFieldLabels[field as keyof typeof emptyClinical].toLowerCase()}`}
                            />
                            <div className="mt-2 flex flex-wrap gap-2">
                              {(clinicalSuggestions[field as keyof typeof clinicalSuggestions] || []).slice(0, 8).map((suggestion) => (
                                <button
                                  key={suggestion}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => addClinicalItem(field as ClinicalAssistedField, suggestion)}
                                  className="rounded border border-border-light bg-white px-2 py-1 text-xs font-semibold text-subtext-light hover:bg-primary/10 hover:text-primary"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <textarea value={(clinical as any)[field]} onChange={(e) => setClinical((prev) => ({ ...prev, [field]: e.target.value }))} className="min-h-28 w-full rounded border border-border-light bg-background-light p-3" />
                        )}
                      </label>
                    ))}
                  </div>

                  <aside className="rounded border border-border-light bg-background-light p-4">
                    <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-subtext-light">Previous Visits</h4>
                    <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
                      {visitHistory.length ? visitHistory.map((visit) => {
                        const record = visit.clinical_record;
                        return (
                          <Link key={visit.id} to={`/patients/history?patient_id=${visit.patient_id}&visit_id=${visit.id}`} className="block rounded bg-white p-3 text-sm hover:bg-primary/5">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <span className="font-bold text-text-light">{formatVisitLabel(visit)}</span>
                              <span className="rounded-full bg-background-light px-2 py-1 text-xs font-semibold text-subtext-light">{statusLabel(visit.status)}</span>
                            </div>
                            <div className="space-y-2">
                              <div>
                                <div className="text-xs font-bold uppercase text-subtext-light">Complaint</div>
                                <p className="text-text-light">{record?.chief_complaint || "-"}</p>
                              </div>
                              <div>
                                <div className="text-xs font-bold uppercase text-subtext-light">History</div>
                                <p className="text-text-light">{record?.history_of_present_illness || record?.notes || "-"}</p>
                              </div>
                            </div>
                          </Link>
                        );
                      }) : (
                        <div className="rounded bg-white p-3 text-sm text-subtext-light">No previous visits found.</div>
                      )}
                    </div>
                  </aside>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
                  <section id="consultation-medicine-editor" className="rounded border border-border-light bg-background-light p-4">
                    <h4 className="mb-3 font-bold">{editingMedicineIndex === null ? "Medicines" : "Edit prescription medicine"}</h4>
                    <SearchSelect<Medicine>
                      label="Medicine"
                      fetchUrl={`${BACKEND_URL}/api/medicines/search?sort=expiry_asc`}
                      displayKey="name"
                      value={editingMedicineIndex === null ? undefined : medicineDraft.medicine_name}
                      clearSignal={medicineSearchClear}
                      onSelect={(medicine: any) => setMedicineDraft((prev) => ({
                        ...prev,
                        medicine_id: medicine.id,
                        medicine_name: medicineDisplayName(medicine),
                        medicine,
                        instruction_before_meals: medicine.default_meal_instruction === "before_meal",
                        instruction_after_meals: medicine.default_meal_instruction === "after_meal",
                        instruction_details: [
                          ...prev.instruction_details.filter((detail) => !["instruction_before_meals", "instruction_after_meals"].includes(detail.key)),
                          ...instructionOptions.filter((option) =>
                            (option.key === "instruction_before_meals" && medicine.default_meal_instruction === "before_meal") ||
                            (option.key === "instruction_after_meals" && medicine.default_meal_instruction === "after_meal")
                          ),
                        ],
                        count_per_time: "1",
                        dosage: "",
                        quantity: medicineDoseConfig(medicine).autoQuantity ? medicineCountFromDays(prev.frequency, prev.duration, 1) : 1,
                        unitPrice: medicine.mrpPrice || 0,
                        batchNo: medicine.batchNo,
                        expiryDate: medicine.expiryDate,
                      }))}
                    />
                    <div className="mt-3 grid grid-cols-1 gap-2">
                      <select
                        value={medicineDraft.frequency}
                        onChange={(e) => updateMedicineFrequency(e.target.value)}
                        className="rounded border border-border-light bg-white p-2"
                      >
                        <option value="">Select frequency</option>
                        {frequencyOptions.map((option) => (
                          <option key={option.code} value={`${option.label} | ${option.code} | ${option.value}`}>
                            {option.label} ({option.code}) - {option.value}
                          </option>
                        ))}
                      </select>
                      {medicineDraft.frequency === onceDailyFrequencyValue && (
                        <div className="rounded border border-border-light bg-white p-2">
                          <div className="mb-2 text-sm font-semibold">Once daily timing</div>
                          <div className="flex flex-wrap gap-2">
                            {onceDailyTimingOptions.map((option) => (
                              <button
                                key={option.code}
                                type="button"
                                onClick={() => setMedicineDraft((prev) => ({ ...prev, once_daily_timing: option.code }))}
                                className={`rounded border px-3 py-2 text-sm font-semibold ${
                                  medicineDraft.once_daily_timing === option.code
                                    ? "border-primary bg-primary text-white"
                                    : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-2 rounded border border-border-light bg-white p-2 sm:grid-cols-3">
                        <label>
                          <div className="mb-1 text-sm font-semibold">{medicineDoseConfig(medicineDraft.medicine).custom ? "Dose per time (include unit)" : `Dose per time (${medicineDoseConfig(medicineDraft.medicine).plural})`}</div>
                          {medicineDoseConfig(medicineDraft.medicine).custom ? (
                            <input
                              type="text"
                              value={medicineDraft.dosage}
                              onChange={(e) => setMedicineDraft((prev) => ({ ...prev, dosage: e.target.value }))}
                              placeholder="Prescribed amount and unit"
                              className="w-full rounded border border-border-light bg-background-light p-2"
                            />
                          ) : <input
                            type="number"
                            min={medicineDoseConfig(medicineDraft.medicine).decimal ? "0.1" : "1"}
                            step={medicineDoseConfig(medicineDraft.medicine).decimal ? "any" : "1"}
                            inputMode={medicineDoseConfig(medicineDraft.medicine).decimal ? "decimal" : "numeric"}
                            value={medicineDraft.count_per_time}
                            onChange={(e) => updateMedicineCountPerTime(e.target.value)}
                            className="w-full rounded border border-border-light bg-background-light p-2"
                          />}
                        </label>
                        <label>
                          <div className="mb-1 text-sm font-semibold">{isWeeklyFrequency(medicineDraft.frequency) ? "Weeks" : "Days"}</div>
                          <input
                            type="number"
                            min="1"
                            value={isWeeklyFrequency(medicineDraft.frequency) && medicineDraft.duration ? String(Number(medicineDraft.duration) / 7) : medicineDraft.duration}
                            onChange={(e) => updateMedicineDuration(isWeeklyFrequency(medicineDraft.frequency) && e.target.value ? String(Number(e.target.value) * 7) : e.target.value)}
                            className="w-full rounded border border-border-light bg-background-light p-2"
                            placeholder={isWeeklyFrequency(medicineDraft.frequency) ? "number of weeks" : "number of days"}
                          />
                        </label>
                        <label>
                          <div className="mb-1 text-sm font-semibold">{medicineDoseConfig(medicineDraft.medicine).quantityLabel}</div>
                          <input
                            type="number"
                            min="1"
                            value={medicineDraft.quantity}
                            onChange={(e) => updateMedicineQuantity(e.target.value)}
                            className="w-full rounded border border-border-light bg-background-light p-2"
                          />
                        </label>
                      </div>
                      <div className="rounded border border-border-light bg-white p-2">
                        <div className="mb-2 text-sm font-semibold">Instructions</div>
                        <div className="flex flex-wrap gap-4">
                          {instructionOptions.map((option) => {
                            const selected = medicineDraft.instruction_details.some((detail) => detail.key === option.key);
                            return (
                              <label key={option.key} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => {
                                    setMedicineDraft((prev) => {
                                      const nextDetails = e.target.checked
                                        ? [...prev.instruction_details, option]
                                        : prev.instruction_details.filter((detail) => detail.key !== option.key);
                                      return {
                                        ...prev,
                                        [option.key]: e.target.checked,
                                        instruction_details: nextDetails,
                                      };
                                    });
                                  }}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                        <input
                          value={medicineDraft.instructions}
                          onChange={(e) => setMedicineDraft((prev) => ({ ...prev, instructions: e.target.value }))}
                          className="mt-3 w-full rounded border border-border-light bg-background-light p-2"
                          placeholder="Additional instruction"
                        />
                      </div>
                      <button type="button" onClick={addMedicineDraft} className="rounded bg-primary px-3 py-2 text-sm font-semibold text-white">{editingMedicineIndex === null ? "Add Medicine" : "Update Medicine"}</button>
                      {editingMedicineIndex !== null && <button type="button" onClick={() => { setEditingMedicineIndex(null); setMedicineDraft(emptyMedicineDraft); }} className="rounded border px-3 py-2 text-sm">Cancel edit</button>}
                    </div>
                  </section>

                  <aside className="flex flex-col gap-5">
                    <section className="rounded border border-border-light bg-background-light p-4">
                      <h4 className="mb-3 font-bold">Services</h4>
                      <SearchSelect<SearchOption>
                        label="Service"
                        fetchUrl={`${BACKEND_URL}/api/medicines/search?category=SERVICE`}
                        displayKey="name"
                        clearSignal={serviceSearchClear}
                        onSelect={(service) => setServiceDraft({ ...service, quantity: 1 })}
                      />
                      {serviceDraft && (
                        <label className="mt-3 block text-xs font-semibold text-subtext-light">
                          Quantity
                          <input
                            type="number"
                            min="1"
                            value={serviceDraft.quantity || 1}
                            onChange={(e) => updateServiceQuantity(e.target.value)}
                            className="mt-1 w-full rounded border border-border-light bg-white p-2 text-sm text-text-light"
                          />
                        </label>
                      )}
                      <button type="button" onClick={addServiceDraft} className="mt-3 rounded bg-primary px-3 py-2 text-sm font-semibold text-white">
                        Add Service
                      </button>
                    </section>

                    <section className="rounded border border-border-light bg-background-light p-4">
                      <h4 className="mb-3 font-bold">Labs</h4>
                      <SearchSelect<SearchOption>
                        label="Lab"
                        fetchUrl={`${BACKEND_URL}/api/medicines/search?category=LAB`}
                        displayKey="name"
                        clearSignal={labSearchClear}
                        onSelect={(lab) => setLabDraft(lab)}
                      />
                      <button type="button" onClick={addLabDraft} className="mt-3 rounded bg-primary px-3 py-2 text-sm font-semibold text-white">
                        Add Lab
                      </button>
                    </section>
                  </aside>
                </div>

                <section className="mt-5 rounded border border-border-light bg-background-light p-4">
                  <h4 className="mb-3 font-bold">Added Items</h4>
                  <div className="space-y-2">
                    {visiblePrescriptionItems.map((item, index) => (
                      <div key={`medicine-summary-${item.medicine_name}-${index}`} className="grid grid-cols-[90px_minmax(0,1fr)_auto] items-center gap-3 rounded bg-white p-3 text-sm">
                        <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-bold uppercase text-primary">Medicine</span>
                        <div>
                          <div className="font-semibold">[{medicineCategory(item.medicine)}] {item.medicine_name}</div>
                          <div className="text-xs text-subtext-light">{[`${doseLabel(item)} per time`, item.frequency, item.duration ? prescriptionDurationLabel(item.duration, item.frequency) : "", item.quantity ? `Qty ${item.quantity}` : "", item.instructions].filter(Boolean).join(" · ")}</div>
                        </div>
                        <div className="flex gap-3">
                          <button type="button" onClick={() => editPrescriptionMedicine(index)} className="text-primary">Edit</button>
                          <button type="button" onClick={() => {
                            setPrescriptionItems((prev) => prev.filter((_, i) => i !== index));
                            setEditingMedicineIndex(null); setMedicineDraft(emptyMedicineDraft);
                          }} className="text-alert-red">Remove</button>
                        </div>
                      </div>
                    ))}
                    {visibleServices.map((service, index) => (
                      <div key={`service-summary-${service.name}-${index}`} className="grid grid-cols-[90px_minmax(0,1fr)_auto] items-center gap-3 rounded bg-white p-3 text-sm">
                        <span className="rounded-full bg-success-green/10 px-2 py-1 text-xs font-bold uppercase text-success-green">Service</span>
                        <div>
                          <div className="font-semibold">{service.name}</div>
                          <div className="text-xs text-subtext-light">Qty {service.quantity || 1}</div>
                        </div>
                        <button type="button" onClick={() => setServices((prev) => prev.filter((_, i) => i !== index))} className="text-alert-red">Remove</button>
                      </div>
                    ))}
                    {visibleLabTests.map((lab, index) => (
                      <div key={`lab-summary-${lab.name}-${index}`} className="grid grid-cols-[90px_minmax(0,1fr)_auto] items-center gap-3 rounded bg-white p-3 text-sm">
                        <span className="rounded-full bg-warning-amber/15 px-2 py-1 text-xs font-bold uppercase text-warning-amber">Lab</span>
                        <div className="font-semibold">{lab.name}</div>
                        <button type="button" onClick={() => setLabTests((prev) => prev.filter((_, i) => i !== index))} className="text-alert-red">Remove</button>
                      </div>
                    ))}
                    {!visiblePrescriptionItems.length && !visibleServices.length && !visibleLabTests.length && (
                      <div className="rounded bg-white p-3 text-sm text-subtext-light">No medicines, services, or labs added yet.</div>
                    )}
                  </div>
                </section>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button onClick={printConsultationDocuments} className="inline-flex items-center gap-2 rounded border border-border-light px-4 py-2 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
                    <Printer size={18} />
                    Print
                  </button>
                  <button onClick={saveConsultation} className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 font-semibold text-white">
                    <Save size={18} />
                    Save Consultation
                  </button>
                  <button onClick={sendToBilling} className="inline-flex items-center gap-2 rounded border border-border-light px-4 py-2 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
                    <Send size={18} />
                    Send to Billing
                  </button>
                </div>
              </section>
            )}
          </main>
        </div>
      </div>

      {selectedVisit && (
        <div className="hidden print:block print:w-full print:overflow-visible">
          <BillingPrintDocument
            includeBill={false}
            contact={displayPatient || undefined}
            patientAge={formatAge(displayPatient)}
            date={formatDate()}
            visit={{
              id: selectedVisit.id,
              patient_id: selectedVisit.patient_id,
              status: selectedVisit.status,
              assigned_doctor: assignedDoctor,
              vitals: [summaryVitals],
              clinical_record: {
                chief_complaint: activeClinical.chief_complaint,
                diagnosis: activeClinical.diagnosis,
                notes: activeClinical.on_examination,
                follow_up_notes: activeClinical.advice,
                follow_up_date: activeClinical.follow_up_date,
              },
              prescription: { id: selectedVisit.prescription?.id || 0, items: visiblePrescriptionItems },
            }}
          />
        </div>
      )}
    </SidebarLayout>
  );
};

export default PatientIntake;
